import { useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'

// ── Token hook (used by all child pages) ──────────────────────────────────────

export function useAdminToken(): string {
  return useAdminStore.getState().token ?? ''
}

export function useAdminGuard() {
  const { clearSession } = useAdminStore()
  const navigate = useNavigate()
  return (response: Response) => {
    if (response.status === 401) {
      clearSession()
      navigate('/admin/login', { replace: true })
    }
  }
}

// ── Sidebar nav link ──────────────────────────────────────────────────────────

function SideLink({ to, icon, label, end }: { to: string; icon: React.ReactNode; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-brand-cyan text-brand-midnight shadow-xs'
            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  )
}

// ── Layout ────────────────────────────────────────────────────────────────────

export default function AdminLayout() {
  const navigate  = useNavigate()
  const { token, user, clearSession, isSessionValid } = useAdminStore()

  useEffect(() => {
    if (!isSessionValid()) {
      clearSession()
      navigate('/admin/login', { replace: true })
    }
  }, [token, isSessionValid, clearSession, navigate])

  if (!isSessionValid()) return null

  const handleLogout = () => {
    clearSession()
    navigate('/admin/login', { replace: true })
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-52 shrink-0 flex flex-col bg-slate-900 text-slate-100">
        {/* Header */}
        <div className="px-4 py-5 border-b border-slate-700/60">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand-cyan rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-brand-midnight" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-bold text-white leading-tight">I-SOFT</p>
              <p className="text-xs text-slate-400 leading-tight">Admin panel</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          <SideLink
            to="/admin/overview"
            label="Pregled"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            }
          />
          <SideLink
            to="/admin/tenants"
            label="Tenanti"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          />
          <SideLink
            to="/admin/users"
            label="Tim"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
        </nav>

        {/* User + footer */}
        <div className="px-3 py-4 border-t border-slate-700/60 space-y-1">
          {user && (
            <div className="flex items-center gap-2.5 px-3 py-2 mb-1">
              {user.picture
                ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full shrink-0" />
                : <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 shrink-0">
                    {(user.fullName ?? user.email)[0].toUpperCase()}
                  </div>
              }
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-200 truncate">{user.fullName ?? user.email}</p>
                <p className="text-xs text-slate-500 truncate">{user.isoftRoles[0] ?? ''}</p>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Nazad na app
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Odjaviti se
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-gray-50">
        <Outlet />
      </main>
    </div>
  )
}
